/*
 * Hero banner slideshow.
 *
 * Only the images rotate. The heading and buttons are the section's own
 * blocks and never move, so there is no reason to pause on hover: nobody is
 * racing a slide to reach "Shop Dog". It does pause while keyboard focus is
 * inside the banner, while the tab is hidden, while a slide is selected in the
 * theme editor, and whenever the visitor presses pause - moving content that
 * runs on its own needs a way to stop it (WCAG 2.2.2).
 *
 * Timing comes from the CSS progress bar on the active dot rather than a JS
 * timer: the slide advances when that animation ends. Pausing is then just
 * animation-play-state, so the bar and the slide change can never drift apart,
 * and a paused bar resumes from where it stopped instead of starting over.
 */
if (!customElements.get('hero-slider')) {
  customElements.define(
    'hero-slider',
    class HeroSlider extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('[data-hb-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-hb-dot]'));
        this.pauseButton = this.querySelector('[data-hb-pause]');
        if (this.slides.length < 2) return;

        this.index = 0;
        this.holds = new Set();
        this.autoplay = this.dataset.autoplay === 'true';
        /* Respect a reduced-motion preference by starting paused; the visitor
           can still press play. */
        this.userPaused =
          this.autoplay && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));
        if (this.pauseButton) {
          this.pauseButton.addEventListener('click', () => {
            this.userPaused = !this.userPaused;
            this.render();
          });
        }

        this.addEventListener('animationend', (event) => {
          if (event.animationName !== 'hb-progress') return;
          if (!event.target.closest('.hb-dot.is-active')) return;
          this.advance();
        });

        /* Keyboard focus only. A mouse click on a dot also focuses it in some
           browsers (Chrome, not Safari), which would leave the slideshow held
           after the click - and behaving differently per browser. */
        this.addEventListener('focusin', (event) => {
          let keyboard = true;
          try {
            keyboard = event.target.matches(':focus-visible');
          } catch (e) {
            /* :focus-visible unsupported: treat all focus as keyboard focus */
          }
          if (keyboard) this.hold('focus', true);
        });
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.hold('focus', false);
        });

        /* Read once at start-up as well: a page opened in a background tab
           never fires visibilitychange, and would rotate unseen. */
        this.onVisibility = () => this.hold('hidden', document.hidden);
        document.addEventListener('visibilitychange', this.onVisibility);
        if (document.hidden) this.holds.add('hidden');

        /* Swipe on touch screens. Passive listeners, and only a clearly
           horizontal movement counts, so vertical scrolling is never blocked
           and a tap on a button is never mistaken for a swipe. */
        let startX = null;
        let startY = null;
        this.addEventListener(
          'touchstart',
          (event) => {
            startX = event.touches[0].clientX;
            startY = event.touches[0].clientY;
          },
          { passive: true }
        );
        this.addEventListener(
          'touchend',
          (event) => {
            if (startX === null) return;
            const dx = event.changedTouches[0].clientX - startX;
            const dy = event.changedTouches[0].clientY - startY;
            startX = null;
            if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
              this.go(this.index + (dx < 0 ? 1 : -1));
            }
          },
          { passive: true }
        );

        /* Theme editor: selecting a Slide block shows it and holds it there,
           so an image can be swapped without the slideshow moving on. */
        if (window.Shopify && window.Shopify.designMode) {
          this.onBlockSelect = (event) => {
            const i = this.slides.findIndex((s) => s.dataset.blockId === event.detail.blockId);
            if (i === -1) return;
            this.go(i);
            this.hold('editor', true);
          };
          this.onBlockDeselect = () => this.hold('editor', false);
          document.addEventListener('shopify:block:select', this.onBlockSelect);
          document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
        }

        this.render();
      }

      disconnectedCallback() {
        if (this.fade) this.fade.finish();
        if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
        if (this.onBlockSelect) {
          document.removeEventListener('shopify:block:select', this.onBlockSelect);
          document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
        }
      }

      hold(reason, on) {
        if (on) this.holds.add(reason);
        else this.holds.delete(reason);
        this.render();
      }

      /* Never fade in a slide whose image has not arrived - on a slow
         connection that would cross-fade to an empty frame. */
      advance() {
        const next = (this.index + 1) % this.slides.length;
        const img = this.slides[next].querySelector('img');
        if (img && !img.complete) {
          /* Unless the visitor has picked a slide themselves in the meantime. */
          const from = this.index;
          const go = () => {
            if (this.index === from) this.go(next);
          };
          img.addEventListener('load', go, { once: true });
          img.addEventListener('error', go, { once: true });
          return;
        }
        this.go(next);
      }

      go(target) {
        const count = this.slides.length;
        const next = ((target % count) + count) % count;
        if (next === this.index) return;

        /* A fade still in flight is completed first, so the next one always
           starts from a settled state: one opaque slide, nothing half-done. */
        if (this.fade) this.fade.finish();

        const previous = this.index;
        this.index = next;
        this.render(previous);

        /* The outgoing slide stays fully opaque underneath while the new one
           fades in over it, and is released only once the fade has finished.
           Fading both at once would let the banner's background show through
           halfway - a visible flash on every change.

           An explicit 0 -> 1 animation rather than a CSS transition. A
           transition only runs when the computed opacity actually changes, so
           returning to a slide that was still fully opaque underneath (a quick
           second click, or autoplay firing mid-fade) started no fade and never
           reported finishing. Its outgoing slide was never released, and every
           change after that was a hard cut. An animation always runs, and
           always settles - finished or cancelled. */
        const incoming = this.slides[next];
        const outgoing = this.slides[previous];
        const release = () => outgoing.classList.remove('is-leaving');

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduceMotion || typeof incoming.animate !== 'function') {
          release();
          return;
        }
        const fade = incoming.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: 900,
          easing: 'ease',
        });
        this.fade = fade;
        fade.finished.then(release, release).then(() => {
          if (this.fade === fade) this.fade = null;
        });
      }

      render(previous) {
        /* Only a slide change touches is-leaving. render() also runs when the
           slideshow pauses (focus, a hidden tab), and clearing it there would
           drop the outgoing slide in the middle of a fade. */
        this.slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i === this.index);
          if (previous !== undefined) slide.classList.toggle('is-leaving', i === previous);
        });
        this.dots.forEach((dot, i) => {
          const active = i === this.index;
          dot.classList.toggle('is-active', active);
          if (active) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });

        this.toggleAttribute('data-autoplaying', this.autoplay);
        this.classList.toggle('is-paused', this.userPaused || this.holds.size > 0);
        this.classList.toggle('is-user-paused', this.userPaused);
        if (this.pauseButton) {
          this.pauseButton.setAttribute(
            'aria-label',
            this.userPaused ? 'Play slideshow' : 'Pause slideshow'
          );
        }
      }
    }
  );
}
